import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Input } from "../../shared/ui";
import { Save, X, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { localTodayISO } from "../../lib/localDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm).toISOString();
};

/**
 * Admin / manager / office / supervisor can create a missing time entry for
 * any worker on any day — including days the system says they weren't clocked
 * on. The entry is created in time_entries AND mirrored into the matching
 * weekly timesheet (creating the timesheet if needed).
 */
export const AddMissingEntryButton = ({ userId, userName, dateIso, jobs, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: dateIso || localTodayISO(),
    start: "07:00",
    end: "15:00",
    job_id: "",
    work_description: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dateIso) setForm((f) => ({ ...f, date: dateIso }));
  }, [dateIso]);

  const save = async () => {
    if (!userId) {
      toast.error("No worker selected");
      return;
    }
    const ci = combineDateTime(form.date, form.start);
    const co = combineDateTime(form.date, form.end);
    if (!ci || !co) {
      toast.error("Pick a date and times");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/time/admin-create-entry`, {
        user_id: userId,
        clock_in: ci,
        clock_out: co,
        job_id: form.job_id || null,
        work_description: form.work_description || null,
      });
      toast.success("Entry added");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create entry");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] font-bold uppercase text-green-700 hover:text-green-900 hover:underline px-2 py-1 inline-flex items-center gap-1"
        data-testid={`add-missing-entry-${dateIso || "nodate"}`}
      >
        <UserPlus className="w-3 h-3" /> Add missing shift
      </button>
    );
  }

  return (
    <div className="p-3 bg-green-50 border-2 border-green-300 rounded space-y-2" data-testid="add-missing-entry-form">
      <div className="text-xs uppercase font-bold text-green-700">
        Add missing shift for {userName || "worker"}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Date</label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-8 text-xs" data-testid="missing-entry-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Start</label>
          <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="h-8 text-xs" data-testid="missing-entry-start" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Finish</label>
          <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="h-8 text-xs" data-testid="missing-entry-end" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">Job</label>
        <select
          value={form.job_id}
          onChange={(e) => setForm({ ...form, job_id: e.target.value })}
          className="h-8 text-xs w-full bg-white border-2 border-slate-200 focus:border-green-500 focus:outline-none rounded-lg px-2"
          data-testid="missing-entry-job"
        >
          <option value="">— No job —</option>
          {(jobs || []).map((j) => (
            <option key={j.id} value={j.id}>
              {j.title || j.name} {j.client_name ? `· ${j.client_name}` : ""}
            </option>
          ))}
        </select>
      </div>
      <Input
        placeholder="Notes / work description (optional)"
        value={form.work_description}
        onChange={(e) => setForm({ ...form, work_description: e.target.value })}
        className="h-8 text-xs"
        data-testid="missing-entry-notes"
      />
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={saving} data-testid="missing-entry-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="missing-entry-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Adding..." : "Add Shift"}
        </Button>
      </div>
    </div>
  );
};

export default AddMissingEntryButton;
